import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/ui/Logo";
import { LanguageSwitcher } from "@/components/ui/LanguageSwitcher";
import { MegaMenu, type MegaMenuItem } from "@/components/ui/mega-menu";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Shield, 
  Users, 
  Building, 
  Factory, 
  FileText, 
  AlertTriangle,
  GraduationCap,
  Flame,
  Box,
  HardHat,
  Briefcase,
  Monitor,
  Skull,
  ShieldCheck
} from "lucide-react";

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation(['navigation', 'common']);
  const { language } = useLanguage();
  const isRTL = language === 'he';

  // Mega Menu navigation items with groups
  const megaMenuItems: MegaMenuItem[] = [
    { 
      id: "home",
      label: t('navigation:home'), 
      href: "/" 
    },
    { 
      id: "safety-officer",
      label: t('navigation:safetyOfficerServices'), 
      href: "/services/safety-officer",
      subMenus: [
        {
          title: t('navigation:megaMenu.safetyServices'),
          items: [
            { 
              label: t('navigation:dropdown.safetyOfficer.main'), 
              href: "/services/safety-officer",
              icon: Shield
            },
            { 
              label: t('navigation:dropdown.safetyOfficer.externalConsulting'), 
              href: "/services/external-consulting",
              icon: Users
            },
            { 
              label: t('navigation:dropdown.safetyOfficer.chooseSafetyOfficer'), 
              href: "/services/choose-safety-officer",
              icon: ShieldCheck
            },
            { 
              label: t('navigation:dropdown.safetyOfficer.importance'), 
              href: "/services/safety-officer-importance",
              icon: AlertTriangle
            },
          ]
        },
        {
          title: t('navigation:megaMenu.bySector'),
          items: [
            { 
              label: t('navigation:dropdown.safetyOfficer.industry'), 
              href: "/services/industry-safety-officer",
              icon: Factory
            },
            { 
              label: t('navigation:dropdown.safetyOfficer.construction'), 
              href: "/services/construction-safety-officer",
              icon: Building
            },
            { 
              label: t('navigation:dropdown.safetyOfficer.roles'), 
              href: "/services/safety-officer-roles",
              icon: Briefcase
            },
            { 
              label: t('navigation:dropdown.safetyOfficer.scope'), 
              href: "/services/safety-officer-scope",
              icon: FileText
            },
          ]
        },
        {
          title: t('navigation:megaMenu.additionalServices'),
          items: [
            { 
              label: t('navigation:dropdown.safetyOfficer.educationAudits'), 
              href: "/services/education-safety-audits",
              icon: GraduationCap
            },
            { 
              label: t('navigation:dropdown.safetyOfficer.fieldFile'), 
              href: "/services/field-file",
              icon: FileText
            },
            { 
              label: t('navigation:dropdown.safetyOfficer.incidentInvestigation'), 
              href: "/services/incident-investigation",
              icon: AlertTriangle
            },
          ]
        },
      ]
    },
    { 
      id: "trainings",
      label: t('navigation:safetyTrainings'), 
      href: "/trainings"
    },
    { 
      id: "consulting",
      label: t('navigation:consulting'), 
      href: "/consulting" 
    },
    { 
      id: "industries",
      label: t('navigation:industries'), 
      href: "/industries" 
    },
    { 
      id: "about",
      label: t('navigation:about'), 
      href: "/about" 
    },
    { 
      id: "blog",
      label: t('navigation:blog'), 
      href: "/blog" 
    },
    { 
      id: "contact",
      label: t('navigation:contact'), 
      href: "/contact" 
    },
  ];

  // Flat navigation for mobile
  const mobileNavigation = [
    { name: t('navigation:home'), href: "/" },
    { 
      name: t('navigation:safetyOfficerServices'), 
      href: "/services/safety-officer",
      dropdownId: "safety-officer",
      children: [
        { name: t('navigation:dropdown.safetyOfficer.main'), href: "/services/safety-officer" },
        { name: t('navigation:dropdown.safetyOfficer.externalConsulting'), href: "/services/external-consulting" },
        { name: t('navigation:dropdown.safetyOfficer.chooseSafetyOfficer'), href: "/services/choose-safety-officer" },
        { name: t('navigation:dropdown.safetyOfficer.importance'), href: "/services/safety-officer-importance" },
        { name: t('navigation:dropdown.safetyOfficer.roles'), href: "/services/safety-officer-roles" },
        { name: t('navigation:dropdown.safetyOfficer.industry'), href: "/services/industry-safety-officer" },
        { name: t('navigation:dropdown.safetyOfficer.construction'), href: "/services/construction-safety-officer" },
        { name: t('navigation:dropdown.safetyOfficer.scope'), href: "/services/safety-officer-scope" },
        { name: t('navigation:dropdown.safetyOfficer.educationAudits'), href: "/services/education-safety-audits" },
        { name: t('navigation:dropdown.safetyOfficer.fieldFile'), href: "/services/field-file" },
        { name: t('navigation:dropdown.safetyOfficer.incidentInvestigation'), href: "/services/incident-investigation" },
      ]
    },
    { name: t('navigation:safetyTrainings'), href: "/trainings" },
    { name: t('navigation:consulting'), href: "/consulting" },
    { name: t('navigation:industries'), href: "/industries" },
    { name: t('navigation:about'), href: "/about" },
    { name: t('navigation:blog'), href: "/blog" },
    { name: t('navigation:contact'), href: "/contact" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (href: string) => location.pathname === href || location.pathname.startsWith(href.split('#')[0]);

  const handleNavigate = (href: string) => {
    navigate(href);
  };

  const toggleMobileDropdown = (dropdownId: string) => {
    setMobileDropdownOpen(prev => prev === dropdownId ? null : dropdownId);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled 
          ? 'bg-primary/95 backdrop-blur-xl shadow-lg py-2' 
          : 'bg-primary/80 backdrop-blur-md py-3 md:py-4'
      }`}
      style={{ paddingTop: 'max(env(safe-area-inset-top), 0px)' }}
    >
      <nav className="container mx-auto flex items-center justify-between px-4 md:px-8">
        {/* Logo */}
        <Link to="/" className="relative z-10 flex flex-col items-center">
          <Logo 
            variant="light" 
            size="sm"
            className="md:hidden"
          />
          <Logo 
            variant="light" 
            size="md"
            showTagline
            className="hidden md:flex text-center"
          />
        </Link>

        {/* Desktop Navigation - Mega Menu */}
        <div className="hidden lg:flex items-center">
          <MegaMenu 
            items={megaMenuItems}
            isActive={isActive}
            onNavigate={handleNavigate}
            isRTL={isRTL}
          />
        </div>

        {/* Desktop CTAs */}
        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher variant="header" />
          <Link to="/auth">
            <Button 
              variant="ghost" 
              size="sm"
              className="font-medium text-primary-foreground/85 hover:text-primary-foreground hover:bg-primary-foreground/10"
            >
              {t('common:buttons.login')}
            </Button>
          </Link>
          <Link to="/contact">
            <Button variant="cta" className="gap-2 shadow-lg hover:shadow-xl transition-all duration-300">
              <Phone className="w-4 h-4" />
              {t('common:buttons.scheduleCall')}
            </Button>
          </Link>
        </div>

        {/* Mobile menu button */}
        <button
          className="lg:hidden p-2.5 rounded-xl transition-all duration-300 hover:bg-primary-foreground/10 text-primary-foreground"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={t('common:menu')}
        >
          {mobileMenuOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <Menu className="w-6 h-6" />
          )}
        </button>
      </nav>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="lg:hidden overflow-hidden"
          >
            <div className="bg-card border-t border-border shadow-2xl overflow-y-auto max-h-[85vh]">
              <div className="container mx-auto py-4 px-4 space-y-1"
                style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' }}
              >
                {mobileNavigation.map((item) => (
                  <div key={item.name}>
                    {item.children ? (
                      <>
                        <button
                          onClick={() => toggleMobileDropdown(item.dropdownId!)}
                          className={`w-full flex items-center justify-between px-4 py-3 rounded-xl font-medium transition-all duration-200 min-h-[48px] ${
                            isActive(item.href)
                              ? "bg-accent/10 text-accent"
                              : "text-foreground hover:bg-secondary"
                          }`}
                        >
                          {item.name}
                          <ChevronDown 
                            className={`w-5 h-5 transition-transform duration-300 ${
                              mobileDropdownOpen === item.dropdownId ? 'rotate-180' : ''
                            }`} 
                          />
                        </button>
                        
                        <AnimatePresence>
                          {mobileDropdownOpen === item.dropdownId && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className={`mt-1 space-y-0.5 ${isRTL ? 'mr-4 border-r-2 pr-4' : 'ml-4 border-l-2 pl-4'} border-accent/30`}>
                                {item.children.map((child) => (
                                  <Link
                                    key={child.name}
                                    to={child.href}
                                    className="block px-4 py-2.5 text-sm text-muted-foreground hover:text-accent transition-colors rounded-lg hover:bg-accent/5 min-h-[44px] flex items-center"
                                    onClick={() => setMobileMenuOpen(false)}
                                  >
                                    {child.name}
                                  </Link>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <Link
                        to={item.href}
                        className={`block px-4 py-3 rounded-xl font-medium transition-all duration-200 min-h-[48px] flex items-center ${
                          isActive(item.href)
                            ? "bg-accent/10 text-accent"
                            : "text-foreground hover:bg-secondary"
                        }`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    )}
                  </div>
                ))}
                
                {/* Mobile CTAs */}
                <div className="pt-6 border-t border-border space-y-3">
                  <LanguageSwitcher variant="mobile" />
                  <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full">
                      {t('common:buttons.login')}
                    </Button>
                  </Link>
                  <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="cta" className="w-full gap-2">
                      <Phone className="w-4 h-4" />
                      {t('common:buttons.scheduleCall')}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
